import { ArrowLeft, Clock, Loader2, Sparkles, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardBody, Badge } from '@neurograph/primitives';
import { useStudio } from '../providers/StudioContext';

export function ScriptPanel() {
  const {
    script,
    scriptGenerating,
    angles,
    generationError,
    setActivePanel,
    generateScript,
  } = useStudio();

  if (scriptGenerating) {
    return (
      <div className="text-center py-16 px-8">
        <Loader2 size={32} className="text-primary-500 mx-auto mb-4 animate-spin" />
        <h3 className="text-base font-semibold text-neutral-700 mb-1">Generating Script</h3>
        <p className="text-sm text-neutral-400">Writing segmented script from your angle…</p>
      </div>
    );
  }

  if (!script) {
    const firstAngle = angles[0];
    return (
      <div className="text-center py-16 px-8">
        <Sparkles size={36} className="text-neutral-200 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-neutral-700 mb-2">No Script Generated</h3>
        <p className="text-neutral-500 mb-6 text-sm max-w-sm mx-auto">
          {angles.length > 0
            ? 'Select an angle to generate a segmented UGC script with delivery notes and visual direction.'
            : 'Generate creative angles first, then select one to script.'}
        </p>
        {generationError && (
          <div className="flex items-center gap-2 justify-center text-xs text-red-600 mb-4">
            <AlertCircle size={12} />
            <span>{generationError}</span>
          </div>
        )}
        <div className="flex items-center justify-center gap-3">
          {firstAngle && (
            <button
              onClick={() => generateScript(firstAngle)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors"
            >
              <Sparkles size={14} />
              Script from Top Angle
            </button>
          )}
          <button
            onClick={() => setActivePanel('angles')}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-neutral-600 border border-neutral-200 rounded-lg hover:bg-neutral-50"
          >
            <ArrowLeft size={14} />
            Back to Angles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-neutral-900">
          {script.title || 'UGC Script'}
        </h2>
        {script.total_duration && (
          <Badge variant="success">
            <Clock size={12} className="mr-1" />
            {script.total_duration}s
          </Badge>
        )}
      </div>

      {/* Segments */}
      {script.segments?.map((seg, i) => (
        <Card key={i}>
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              <span className="text-xs font-semibold text-primary-500">
                Segment {seg.segment_number ?? i + 1}
              </span>
              {seg.duration && (
                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                  {seg.duration}s
                </span>
              )}
            </div>
          </CardHeader>
          <CardBody>
            {seg.visual_direction && (
              <p className="text-xs text-neutral-400 mb-1">{seg.visual_direction}</p>
            )}
            {seg.script_text && (
              <p className="text-sm italic text-neutral-800 leading-relaxed mb-2">
                &ldquo;{seg.script_text}&rdquo;
              </p>
            )}
            {seg.delivery_notes && (
              <p className="text-xs text-neutral-400 italic">{seg.delivery_notes}</p>
            )}
          </CardBody>
        </Card>
      ))}

      {/* Full script */}
      {script.full_script && (
        <Card>
          <CardHeader>
            <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Full Script
            </span>
          </CardHeader>
          <CardBody>
            <pre className="text-sm leading-relaxed whitespace-pre-wrap text-neutral-700 bg-neutral-50 rounded-lg p-4 m-0">
              {script.full_script}
            </pre>
          </CardBody>
        </Card>
      )}

      {/* Hook & CTA alternatives */}
      {(script.hook_alternatives?.length || script.cta_alternatives?.length) && (
        <Card>
          <CardHeader>
            <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Alternatives
            </span>
          </CardHeader>
          <CardBody>
            {script.hook_alternatives && script.hook_alternatives.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-neutral-500 mb-1">Hook Alternatives</p>
                <ul className="space-y-1">
                  {script.hook_alternatives.map((h, i) => (
                    <li key={i} className="text-sm text-neutral-700">• {h}</li>
                  ))}
                </ul>
              </div>
            )}
            {script.cta_alternatives && script.cta_alternatives.length > 0 && (
              <div>
                <p className="text-xs font-medium text-neutral-500 mb-1">CTA Alternatives</p>
                <ul className="space-y-1">
                  {script.cta_alternatives.map((c, i) => (
                    <li key={i} className="text-sm text-neutral-700">• {c}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
